import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { CheckCircle } from '@mui/icons-material';

import { Button, ButtonProps } from '../../Button';
import { usePillRadioButtonGroupContext } from './PillRadioButtonGroup.context';

const useStyles = makeStyles({ name: 'PillRadioButton' })((theme) => ({
  buttonBase: {
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    minWidth: '90px',
    fontWeight: 450,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    }
  },
  buttonDisabled: {
    background: theme.palette.action.disabledBackground,
    color: `${theme.palette.text.primary} !important`
  },
  buttonActive: {
    borderColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.secondary.main} !important`,
  },
}));

export type PillRadioButtonProps = {
  value: string | number | boolean;
  label: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  size?: ButtonProps['size'];
  isChecked?: (value: string | number | boolean) => boolean;
  testId?: string;
};

export const PillRadioButton: React.FC<PillRadioButtonProps> = ({ value, label, disabled, size, isChecked, testId = 'pill-radio-button', ...props }) => {
  const { classes, cx } = useStyles();
  const ctx = usePillRadioButtonGroupContext();

  if (!ctx) {
    return null;
  }

  const active = isChecked ? isChecked(ctx.value) : (value === ctx.value);
  const isDisabled = disabled || ctx.disabled;
  const icon = props.icon ?? <CheckCircle />;

  return (
    <Button
      onClick={() => ctx.onClick(value)}
      classes={{
        root: classes.buttonBase,
      }}
      className={cx({
        [classes.buttonDisabled]: isDisabled,
        [classes.buttonActive]: active,
      })}
      variant="outlined"
      startIcon={active ? icon : undefined}
      disabled={isDisabled}
      size={size}
      data-testid={testId}
    >
      {label}
    </Button>
  );
};
